import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Collapse,
} from '@material-ui/core';
import {
  Search as SearchIcon,
  GetApp as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@material-ui/icons';
import myAxios from '../../../_services/axiositeachWrapper';
import Config from '../../../_config/config';

interface Student {
  enrollmentId: number;
  studentCode: string;
  studentName: string;
  batchId: number;
  batchName: string;
  attendanceValue: number | null;
  attendance: string;
  classStatus: string | null;
}

interface BatchGroup {
  batchId: number;
  batchName: string;
  students: Student[];
  expanded: boolean;
}

interface StudentListDialogProps {
  open: boolean;
  onClose: () => void;
  activitySlotId: number | null;
  activitySlotName?: string;
}

const StudentListDialog: React.FC<StudentListDialogProps> = ({
  open,
  onClose,
  activitySlotId,
  activitySlotName = 'Activity Slot',
}) => {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [batchGroups, setBatchGroups] = useState<BatchGroup[]>([]);

  useEffect(() => {
    if (open && activitySlotId) {
      fetchStudents();
    }
  }, [open, activitySlotId]);

  useEffect(() => {
    // Filter students based on search query
    if (searchQuery.trim() === '') {
      setFilteredStudents(students);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = students.filter(
        (student) =>
          student.studentName.toLowerCase().includes(query) ||
          student.studentCode.toLowerCase().includes(query) ||
          student.batchName.toLowerCase().includes(query)
      );
      setFilteredStudents(filtered);
    }
  }, [searchQuery, students]);

  useEffect(() => {
    // Group students by batch
    const groups: { [key: number]: BatchGroup } = {};
    filteredStudents.forEach((student) => {
      if (!groups[student.batchId]) {
        groups[student.batchId] = {
          batchId: student.batchId,
          batchName: student.batchName,
          students: [],
          expanded: true,
        };
      }
      groups[student.batchId].students.push(student);
    });
    setBatchGroups(Object.values(groups));
  }, [filteredStudents]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response: any = await myAxios.get(
        `${Config.nodeSiteUrl}v1/courses/activitySlots/${activitySlotId}/students`
      );
      if (response.data.success) {
        setStudents(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to load student list');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBatch = (batchId: number) => {
    setBatchGroups((prev) =>
      prev.map((group) =>
        group.batchId === batchId ? { ...group, expanded: !group.expanded } : group
      )
    );
  };

  const getAttendanceChip = (attendance: string, attendanceValue: number | null) => {
    if (attendanceValue === null || attendance === 'Not Marked') {
      return <Chip label="Not Marked" size="small" style={{ backgroundColor: '#9e9e9e', color: 'white' }} />;
    }
    if (attendanceValue === 1 || attendance === 'Present') {
      return <Chip label="Present" size="small" style={{ backgroundColor: '#4caf50', color: 'white' }} />;
    }
    return <Chip label="Absent" size="small" style={{ backgroundColor: '#f44336', color: 'white' }} />;
  };

  const exportToCSV = () => {
    // Create CSV content
    const headers = ['Student Code', 'Student Name', 'Batch', 'Attendance'];
    const rows = filteredStudents.map((student) => [
      student.studentCode,
      student.studentName,
      student.batchName,
      student.attendance || 'Not Marked',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `students_${activitySlotName.replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setSearchQuery('');
    setStudents([]);
    setFilteredStudents([]);
    setBatchGroups([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Students Enrolled - {activitySlotName}</Typography>
          <Button
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            disabled={filteredStudents.length === 0}
            size="small"
          >
            Export CSV
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search Bar */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by student name, code, or batch..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ marginBottom: 20 }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : students.length === 0 ? (
          <Box textAlign="center" padding={4}>
            <Typography color="textSecondary">No students enrolled in this activity slot</Typography>
          </Box>
        ) : filteredStudents.length === 0 ? (
          <Box textAlign="center" padding={4}>
            <Typography color="textSecondary">No students match your search</Typography>
          </Box>
        ) : (
          <>
            {/* Summary */}
            <Box marginBottom={2}>
              <Typography variant="body2" color="textSecondary">
                Showing {filteredStudents.length} of {students.length} students
              </Typography>
            </Box>

            {/* Grouped by Batch */}
            {batchGroups.map((group) => (
              <Box key={group.batchId} marginBottom={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  padding={1}
                  style={{
                    backgroundColor: 'var(--surface-sunken)',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleToggleBatch(group.batchId)}
                >
                  <IconButton size="small">
                    {group.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, flex: 1 }}>
                    {group.batchName} ({group.students.length} students)
                  </Typography>
                </Box>

                <Collapse in={group.expanded}>
                  <TableContainer component={Paper} style={{ marginTop: 8 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ fontWeight: 600 }}>Student Code</TableCell>
                          <TableCell style={{ fontWeight: 600 }}>Student Name</TableCell>
                          <TableCell style={{ fontWeight: 600 }}>Attendance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.students.map((student) => (
                          <TableRow key={student.enrollmentId}>
                            <TableCell>{student.studentCode}</TableCell>
                            <TableCell>{student.studentName}</TableCell>
                            <TableCell>
                              {getAttendanceChip(student.attendance, student.attendanceValue)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentListDialog;
